import { css, type Theme } from '@emotion/react';
import styled from '@emotion/styled';
import type { LinkProps } from 'react-router-dom';

import type { ButtonProps } from 'storybookConfig/stories/molecules/Button';
import Button, {
  ButtonKinds,
  ButtonSizes,
  getButtonPadding,
  getButtonTypography,
} from 'storybookConfig/stories/molecules/Button';

type GetButtonColorSchemeArg = Pick<SecondaryButtonProps, '$iconAfter' | 'kind'>;

const getButtonColorScheme = ({
  $iconAfter,
  kind = ButtonKinds.Action,
  theme,
}: GetButtonColorSchemeArg & { theme: Theme }) => {
  if (kind === ButtonKinds.Action) {
    return css`
      background: ${theme.color.surfaceCanvas};
      color: ${theme.color.bodyTextPrimary};
      border-color: ${theme.color.borderStrong};

      &:hover,
      &:focus {
        background: ${theme.color.surfaceSubtle};
        color: ${theme.color.bodyTextPrimary};
        border-color: ${theme.color.borderStrong};
      }

      &:focus::${$iconAfter ? 'before' : 'after'} {
        border-color: ${theme.color.borderStrong};
      }
    `;
  }

  if (kind === ButtonKinds.Neutral) {
    return css`
      background: ${theme.color.surfaceCanvas};
      color: ${theme.color.bodyTextSecondary};
      border-color: ${theme.color.bodyTextSecondary};

      &:hover,
      &:focus {
        background: ${theme.color.surfaceStrong};
        color: ${theme.color.bodyTextPrimary};
        border-color: ${theme.color.controlBorder};
      }

      &:focus::${$iconAfter ? 'before' : 'after'} {
        border-color: ${theme.color.controlBorder};
      }
    `;
  }

  if (kind === ButtonKinds.Destructive) {
    return css`
      background: ${theme.color.surfaceCanvas};
      color: ${theme.color.errorBase};
      border-color: ${theme.color.errorBase};

      &:hover,
      &:focus {
        background: ${theme.color.errorTint};
        color: ${theme.color.errorStrong};
        border-color: ${theme.color.errorStrong};
      }

      &:focus::${$iconAfter ? 'before' : 'after'} {
        border-color: ${theme.color.errorStrong};
      }
    `;
  }

  return css``;
};

interface SecondaryButtonProps extends ButtonProps {
  /**
   * The kind of action this button describes
   */
  kind?: `${ButtonKinds}`;

  /**
   * The HTML element to render the button as
   */
  as?: React.ElementType;

  /**
   * an optional route to navigate to if cast as a Link
   */
  to?: LinkProps['to'];

  /**
   * an optional article route to navigate to if cast as a SupportLink
   */
  article?: string;

  /**
   * an optional target if cast as an a tag
   */
  target?: string;

  /**
   * an optional href if cast as an a tag
   */
  href?: string;
}

/**
 * A composed `Button` component that represents the secondary action a user can take on a page.
 * - `size` is defaulted to `ButtonSizes.Large`
 * - `kind` is defaulted to `ButtonKinds.Action`
 */
const SecondaryButton = styled(Button, {
  shouldForwardProp: (prop) => !['kind', 'as', '$iconName'].includes(prop),
})<SecondaryButtonProps>`
  position: relative;
  border-radius: 100px;
  border: 1px solid transparent;

  /* Set up inset ring for focus, using the pseudo selector not bound to an icon */
  ${({ $iconName, $iconAfter }) => css`
    &::${$iconName && $iconAfter ? 'before' : 'after'} {
      position: absolute;
      inset: 3px;
      display: block;
      content: '';
      border: 1px solid transparent;
      border-radius: 100px;
    }
  `}

  /* Determine appropriate typography */
  ${({ size = ButtonSizes.Large }) => getButtonTypography({ size })}

  /* Determine appropriate padding */
  ${({ size = ButtonSizes.Large, $iconName, $iconAfter }) =>
    getButtonPadding({ size, $iconName, $iconAfter })}

  /* Determine color scheme */
  ${({ kind, $iconAfter, theme }) => getButtonColorScheme({ kind, $iconAfter, theme })}

  /* Maintain a static disabled state */
  &:disabled {
    background: transparent;
    color: ${({ theme }) => theme.color.bodyTextDisabled};
    border-color: ${({ theme }) => theme.color.bodyTextDisabled};
  }
`;

export default SecondaryButton;
